// Text lines in the coach crisis cards.
//
// askCoach hands each crisis line to the apps as { name, detail, phone?, text?,
// url } (the shape sync-knowledge-hub.mjs writes). `text` is the instruction
// for reaching the line by SMS: "Text HELLO to 50808", "Call or text 988".
// The cards used to render name, detail, phone and url only, so the generator
// also leads `detail` with the instruction. A card that renders `text` itself
// maps over coachCardLines(resources), which drops that lead, so the
// instruction shows once; on a phone it opens the messaging app ready to send.
//
// Apps import this file by relative path, like the rest of the package. It
// has no dependencies and builds no tables, so a coach panel can import it
// without pulling the whole crisis table into its chunk.

const TEXT_KEYWORD = /^Text (\w+) to (\d+)$/;
const CALL_OR_TEXT = /^Call or text (\d+)$/;

/**
 * "Text HELLO to 50808" → { number: '50808', keyword: 'HELLO' };
 * "Call or text 988" → { number: '988', keyword: null }.
 * Anything else → null, and the card shows the words without a link.
 */
export const parseTextLine = (text) => {
  const value = typeof text === 'string' ? text.trim() : '';
  const keyed = TEXT_KEYWORD.exec(value);
  if (keyed) return { number: keyed[2], keyword: keyed[1] };
  const plain = CALL_OR_TEXT.exec(value);
  return plain ? { number: plain[1], keyword: null } : null;
};

/**
 * True on a phone, where an sms: link opens the messaging app. On a desktop
 * the same link does nothing or asks which app to use, so the card shows the
 * instruction as words instead.
 */
export const isPhone = (nav = globalThis.navigator) => {
  if (!nav) return false;
  if (nav.userAgentData?.mobile) return true;
  return /Android|iPhone|iPod/i.test(nav.userAgent || '');
};

/**
 * The sms: link for a text line, or null when there shouldn't be one (not a
 * phone, or the text isn't in a form we can turn into a link).
 *
 * `?&body=` is the form both iOS and Android read: iOS ignores the empty
 * first parameter and Android takes `body` from the query.
 */
export const textLineSmsHref = (text, nav = globalThis.navigator) => {
  const line = parseTextLine(text);
  if (!line || !isPhone(nav)) return null;
  return line.keyword ? `sms:${line.number}?&body=${encodeURIComponent(line.keyword)}` : `sms:${line.number}`;
};

/**
 * The detail to show beside a line whose `text` the card renders itself:
 * "Text HELLO to 50808. Free, anonymous…" → "Free, anonymous…". A detail
 * that doesn't lead with the instruction (the hub's shape before the shared
 * table) comes back unchanged, so a card can call this on every line.
 */
export const coachLineDetail = ({ detail = '', text } = {}) => {
  const lead = text ? `${text}. ` : null;
  return lead && detail.startsWith(lead) ? detail.slice(lead.length) : detail;
};

/**
 * The lines as a coach card shows them: each detail passed through
 * coachLineDetail, so the card can render `r.detail` and `r.text` side by
 * side without repeating the instruction. Returns new objects; the hub's
 * lines are frozen and shared.
 */
export const coachCardLines = (resources = []) =>
  resources.map((line) => (line.text ? { ...line, detail: coachLineDetail(line) } : line));
